import React, { useState } from 'react';
import Modal from './Modal';
import { safeHref } from '../utils/safeUrl';
import { formatDateTime } from '../utils/dates';

/**
 * <AnswerValue /> — renders one form-submission answer.
 *
 * Photo, signature and file answers are stored as data URLs. An uploaded file
 * renders as the thing itself: images as a thumbnail you can open full-size
 * (photo questions can hold several), signatures with the signer's name, role and
 * time, other files (PDFs, docs) as a labelled chip you can open or save.
 *
 * Anything that isn't a file renders as plain text.
 */

const DATA_URL = /^data:([^;,]+)[;,]/i;
const isDataUrl = (v) => typeof v === 'string' && DATA_URL.test(v);
const mimeOf = (v) => (isDataUrl(v) ? (v.match(DATA_URL)[1] || '').toLowerCase() : '');
// The File field keeps the original filename in the URL fragment (see FormFill).
const nameOf = (v) => {
  const m = typeof v === 'string' && v.match(/#name=([^&]+)$/);
  try { return m ? decodeURIComponent(m[1]) : ''; } catch { return ''; }
};
const isImage = (v) => mimeOf(v).startsWith('image/');
// Files may also arrive as a plain URL to something we've already stored.
const isFileUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v) && /\.(pdf|docx?|xlsx?|csv|txt|png|jpe?g|webp|gif|heic)(\?|$)/i.test(v);
const isImageUrl = (v) => isFileUrl(v) && /\.(png|jpe?g|webp|gif|heic)(\?|$)/i.test(v);
const isPicture = (v) => isImage(v) || isImageUrl(v);

const EXT = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/csv': 'CSV',
  'text/plain': 'TXT',
};
const kindOf = (v) => EXT[mimeOf(v)] || (mimeOf(v).split('/')[1] || 'FILE').toUpperCase();

/** Rough byte size of a base64 data URL, for the "2.4 MB" hint on a file chip. */
const sizeOf = (v) => {
  const i = v.indexOf(',');
  if (i < 0) return null;
  const bytes = Math.round((v.length - i - 1) * 0.75);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const imgHref = (src) => (isDataUrl(src) ? src : safeHref(src));

/** Full-size view of a photo, in the workspace dialog (focus trapped, Escape closes). */
const Lightbox = ({ src, label, onClose }) => (
  <Modal
    title={label || 'Photo'}
    maxWidth={980}
    onClose={onClose}
    footer={({ requestClose }) => (
      <>
        {imgHref(src) && (
          <a className="cw-btn cw-btn-secondary" href={imgHref(src)} download={`${(label || 'photo').replace(/[^\w.-]+/g, '-')}.jpg`} style={{ textDecoration: 'none' }}>
            <i className="fa-solid fa-download" aria-hidden="true" /> Download
          </a>
        )}
        <button type="button" className="cw-btn cw-btn-primary" onClick={requestClose}>Close</button>
      </>
    )}
  >
    <img src={src} alt={label || 'Evidence photo'} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 10, display: 'block', margin: '0 auto' }} />
  </Modal>
);

/** A flagged answer is a finding — show its priority and note, not just the value. */
const Finding = ({ answer }) => {
  if (!answer?.flagged) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
      <span className="cw-chip" style={{ color: '#fff', background: 'var(--cw-danger, #dc2626)' }}>
        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> {answer.priority ? `Finding · ${answer.priority}` : 'Finding · no priority'}
      </span>
      {answer.priorityNote && <span style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', color: 'var(--cw-danger-fg, #b91c1c)' }}>{answer.priorityNote}</span>}
    </div>
  );
};

const Thumb = ({ src, label, maxHeight, onOpen }) => (
  <button type="button" onClick={onOpen} aria-label={`View ${label || 'photo'} full size`}
    style={{ padding: 0, border: 'none', background: 'none', cursor: 'zoom-in', display: 'block', maxWidth: '100%' }}>
    <img src={src} alt={label || 'Evidence photo'}
      style={{ maxWidth: '100%', maxHeight, borderRadius: 8, border: '1px solid var(--cw-border)', display: 'block' }} />
  </button>
);

const SignerLine = ({ signer }) => {
  if (!signer) return null;
  const who = [signer.name, signer.role].filter(Boolean).join(', ');
  return (
    <div className="cw-meta" style={{ marginTop: 4 }}>
      {who ? `Signed by ${who}` : 'Signed'}{signer.signedAt ? ` · ${formatDateTime(signer.signedAt)}` : ''}{signer.method === 'typed' ? ' · typed signature' : ''}
    </div>
  );
};

/** A repeating list answer as a small table (rows keyed by column id). */
const RowsTable = ({ answer, label }) => {
  const cols = Array.isArray(answer.columns) ? answer.columns : [];
  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <table className="cw-table" style={{ fontSize: 'var(--cs-text-sm, 0.8125rem)', borderCollapse: 'collapse', minWidth: Math.min(cols.length * 110, 640) }}>
        <caption className="cw-sr-only">{label || 'List'}</caption>
        <thead><tr>{cols.map((c) => <th key={c.id} scope="col" style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--cw-border)' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {answer.rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => <td key={c.id} style={{ padding: '4px 8px', borderBottom: '1px solid var(--cw-border-light)' }}>{r && r[c.id] != null && r[c.id] !== '' ? String(r[c.id]) : '—'}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AnswerValue = ({ value, label, maxHeight = 220, answer }) => {
  const [zoom, setZoom] = useState(null);
  const flag = answer?.flagged ? <Finding answer={answer} /> : null;

  if (answer && Array.isArray(answer.rows) && Array.isArray(answer.columns)) {
    return answer.rows.length ? <RowsTable answer={answer} label={label} /> : <>—</>;
  }

  // Several photos on one question.
  if (Array.isArray(value) && value.length && value.every(isPicture)) {
    return (
      <>
        {flag}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(110px, 100%), 1fr))', gap: 8 }}>
          {value.map((src, i) => (
            <Thumb key={i} src={src} label={`${label || 'Photo'} ${i + 1} of ${value.length}`} maxHeight={Math.min(maxHeight, 140)} onOpen={() => setZoom({ src, label: `${label || 'Photo'} ${i + 1}` })} />
          ))}
        </div>
        {zoom && <Lightbox src={zoom.src} label={zoom.label} onClose={() => setZoom(null)} />}
      </>
    );
  }
  if (Array.isArray(value)) return <>{flag}{value.join(', ') || '—'}</>;

  if (isPicture(value)) {
    return (
      <>
        {flag}
        <Thumb src={value} label={label} maxHeight={maxHeight} onOpen={() => setZoom({ src: value, label })} />
        <SignerLine signer={answer?.signer} />
        {zoom && <Lightbox src={zoom.src} label={zoom.label} onClose={() => setZoom(null)} />}
      </>
    );
  }

  if (isDataUrl(value) || isFileUrl(value)) {
    const kind = isDataUrl(value) ? kindOf(value) : (value.split('.').pop().split('?')[0] || 'FILE').toUpperCase();
    const size = isDataUrl(value) ? sizeOf(value) : null;
    let name = nameOf(value);
    if (!name && isFileUrl(value)) { try { name = decodeURIComponent(value.split('/').pop().split('?')[0]); } catch { name = ''; } }
    const href = isDataUrl(value) ? value : safeHref(value);
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
        download={name || (isDataUrl(value) ? `${(label || 'file').replace(/[^\w.-]+/g, '-')}.${kind.toLowerCase()}` : undefined)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, maxWidth: '100%', minHeight: 36,
          border: '1px solid var(--cw-border)', background: 'var(--cw-surface-alt, #f9fafb)', textDecoration: 'none', color: 'var(--cw-text)', fontSize: 'var(--cs-text-sm, 0.8125rem)',
        }}>
        <i className="fa-solid fa-file-arrow-down" aria-hidden="true" style={{ color: 'var(--cw-blue)', flexShrink: 0 }} />
        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name || label || 'Attachment'}
        </span>
        <span className="cw-meta" style={{ flexShrink: 0 }}>{[kind, size].filter(Boolean).join(' · ')}</span>
      </a>
    );
  }

  // Submissions made before photos were stored inline kept only the filename
  // ("_DSC2740.JPG"), so there is no image to show. Say so rather than printing a
  // bare filename that looks like a broken attachment.
  if (typeof value === 'string' && /^[^\s/\\]+\.(png|jpe?g|webp|gif|heic|pdf|docx?|xlsx?)$/i.test(value.trim())) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', fontSize: 'var(--cs-text-sm, 0.8125rem)', color: 'var(--cw-warning-fg, #92400e)' }}>
        <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
        <span style={{ fontWeight: 600 }}>{value.trim()}</span>
        <span>file name only — the file itself was not uploaded</span>
      </span>
    );
  }

  const text = value == null || value === '' ? '' : String(value);
  return <>{flag}{text || '—'}</>;
};

export default AnswerValue;
